import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import logger from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_CREATE_FORM = {
  pricing_version: 'v3', tier: 'premium_portfolio', region_name: '',
  monthly_amount: '', currency: 'USD', symbol: '$', countries: '', is_active: true
};

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

export const useAdminPricing = () => {
  const [pricingConfigs, setPricingConfigs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [managingCountriesId, setManagingCountriesId] = useState(null);
  const [countriesList, setCountriesList] = useState([]);
  const [newCountryInput, setNewCountryInput] = useState('');
  const [createForm, setCreateForm] = useState({ ...EMPTY_CREATE_FORM });

  const loadPricing = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/pricing/all`, { headers: authHeaders() });
      if (!response.ok) throw new Error(`Failed to load pricing (${response.status})`);
      const data = await response.json();
      // Phase D — show v3 flat-per-tier rows (the current model). Legacy v2 rows
      // are already deactivated by the migration and are hidden here to keep
      // the tab focused on the current pricing shape.
      setPricingConfigs(data.v3_two_tier_pricing?.configs || []);
    } catch (error) { toast.error(`Failed to load pricing: ${error.message}`); }
    finally { setIsLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadPricing(); }, []);

  const handleEdit = (config) => {
    setEditingId(config.config_id);
    if (config.pricing_version === 'v3') {
      setEditForm({ monthly_amount: config.monthly_amount, region_name: config.region_name, is_active: config.is_active });
    } else {
      setEditForm({ per_investment_price: config.per_investment_price, floor: config.floor, cap: config.cap, is_active: config.is_active, display_name: config.display_name });
    }
  };

  const cancelEdit = () => { setEditingId(null); setEditForm({}); };

  const handleSaveEdit = async (configId) => {
    try {
      const payload = { ...editForm };
      ['per_investment_price', 'floor', 'cap', 'monthly_amount'].forEach(k => { if (payload[k] !== undefined && payload[k] !== '') payload[k] = parseFloat(payload[k]); });
      Object.keys(payload).forEach(k => { if (payload[k] === undefined) delete payload[k]; });
      const response = await fetch(`${BACKEND_URL}/api/admin/pricing/${configId}`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify(payload) });
      if (!response.ok) throw new Error((await response.json().catch(() => ({}))).detail || 'Failed to update');
      toast.success('Pricing updated successfully!');
      cancelEdit(); loadPricing();
    } catch (error) { toast.error(`Failed to update: ${error.message}`); }
  };

  const handleDelete = async (configId) => {
    if (!window.confirm('Delete this pricing configuration?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/pricing/${configId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete');
      toast.success('Pricing deleted'); loadPricing();
    } catch (err) { logger.error(err); toast.error('Failed to delete pricing'); }
  };

  const handleManageCountries = (config) => { setManagingCountriesId(config.config_id); setCountriesList([...(config.countries || [])]); setNewCountryInput(''); };
  const handleCancelManageCountries = () => { setManagingCountriesId(null); setCountriesList([]); setNewCountryInput(''); };

  const handleAddCountry = () => {
    const trimmed = newCountryInput.trim();
    if (trimmed && !countriesList.includes(trimmed)) { setCountriesList([...countriesList, trimmed]); setNewCountryInput(''); toast.success(`Added "${trimmed}"`); }
    else if (countriesList.includes(trimmed)) toast.error('Country already in list');
  };

  const handleRemoveCountry = (c) => setCountriesList(countriesList.filter(x => x !== c));

  const handleSaveCountries = async (configId) => {
    if (countriesList.length === 0) { toast.error('Add at least one country'); return; }
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/pricing/${configId}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ countries: countriesList })
      });
      if (!response.ok) throw new Error((await response.json().catch(() => ({}))).detail || 'Failed to update');
      toast.success(`${countriesList.length} countries saved`);
      handleCancelManageCountries(); loadPricing();
    } catch (error) { toast.error(`Failed to update countries: ${error.message}`); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.region_name?.trim()) { toast.error('Enter a region name'); return; }
    if (!createForm.monthly_amount || parseFloat(createForm.monthly_amount) <= 0) { toast.error('Enter a valid monthly amount'); return; }
    if (!['premium_portfolio', 'premium_continuity'].includes(createForm.tier)) { toast.error('Pick a tier'); return; }
    try {
      const payload = {
        pricing_version: 'v3', tier: createForm.tier,
        region_name: createForm.region_name.trim(),
        monthly_amount: parseFloat(createForm.monthly_amount),
        currency: createForm.currency.toUpperCase(), symbol: createForm.symbol,
        countries: createForm.countries.split(',').map(c => c.trim()).filter(c => c),
        is_active: createForm.is_active
      };
      const response = await fetch(`${BACKEND_URL}/api/admin/pricing`, { method: 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
      if (!response.ok) throw new Error((await response.json().catch(() => ({}))).detail || 'Failed to create');
      toast.success('Pricing created successfully');
      setShowCreateForm(false); setCreateForm({ ...EMPTY_CREATE_FORM }); loadPricing();
    } catch (err) { logger.error(err); toast.error(`Failed to create pricing: ${err.message}`); }
  };

  const [importPreview, setImportPreview] = useState(null); // { payload, preview, fileName }
  const [importBusy, setImportBusy] = useState(false);

  const handleExport = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/pricing/export`, { headers: authHeaders() });
      if (!response.ok) throw new Error(`Export failed (${response.status})`);
      const payload = await response.json();
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = `pricing-config-${stamp}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${payload.count} pricing rows`);
    } catch (err) {
      logger.error(err);
      toast.error(`Export failed: ${err.message}`);
    }
  };

  const handleImportFilePick = async (file) => {
    if (!file) return;
    setImportBusy(true);
    toast.info('Reading file…');
    try {
      const text = await file.text();
      logger.info('[Import] File read, length:', text.length);
      let payload;
      try { payload = JSON.parse(text); }
      catch (parseErr) {
        logger.error('[Import] JSON.parse failed:', parseErr);
        throw new Error('Selected file is not valid JSON');
      }
      logger.info('[Import] JSON parsed, configs count:', Array.isArray(payload?.configs) ? payload.configs.length : 'INVALID');

      toast.info('Checking against database…');
      const previewRes = await fetch(`${BACKEND_URL}/api/admin/pricing/import/preview`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(payload),
      });
      logger.info('[Import] Preview response status:', previewRes.status);
      const preview = await previewRes.json();
      logger.info('[Import] Preview response body:', preview);
      if (!previewRes.ok) throw new Error(preview.detail || `Preview failed (${previewRes.status})`);

      // Open the confirm modal with the preview result
      setImportPreview({ payload, preview, fileName: file.name });
    } catch (err) {
      logger.error('[Import] Error:', err);
      toast.error(`Import failed: ${err.message}`);
    } finally {
      setImportBusy(false);
    }
  };

  const cancelImport = () => setImportPreview(null);

  const confirmImport = async () => {
    if (!importPreview) return;
    setImportBusy(true);
    try {
      const importRes = await fetch(`${BACKEND_URL}/api/admin/pricing/import`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(importPreview.payload),
      });
      const result = await importRes.json();
      if (!importRes.ok) throw new Error(result.detail || 'Import failed');
      toast.success(`Import complete — ${result.inserted} new, ${result.updated} updated, ${result.unchanged} unchanged`);
      setImportPreview(null);
      loadPricing();
    } catch (err) {
      logger.error('[Import] Confirm error:', err);
      toast.error(`Import failed: ${err.message}`);
    } finally {
      setImportBusy(false);
    }
  };

  return {
    pricingConfigs, isLoading,
    editingId, editForm, setEditForm,
    showCreateForm, setShowCreateForm,
    managingCountriesId, countriesList, newCountryInput, setNewCountryInput,
    createForm, setCreateForm,
    handleEdit, handleSaveEdit, handleDelete, cancelEdit,
    handleManageCountries, handleCancelManageCountries,
    handleAddCountry, handleRemoveCountry, handleSaveCountries,
    handleCreate,
    handleExport, handleImportFilePick, confirmImport, cancelImport,
    importPreview, importBusy,
  };
};
